<script setup lang="ts">
import { useEventListener } from "@vueuse/core";

import LeNgArticleClassic from "@/components/articles/LeNgArticleClassic.vue";
import LeNgArticleCompact from "@/components/articles/LeNgArticleCompact.vue";
import type { SearchViewType } from "@/types";

const props = defineProps<{
  searchViewType: SearchViewType;
  methodId: string;
  prefix: string;
  html: string;
  index: number;
  blurred: boolean;
}>();

const emit = defineEmits<{
  ngSearch: [
    search: {
      query: string;
      methodId: string;
      auto: boolean;
      staticMetadata: string | undefined;
      temporaryMetadata: string[] | undefined;
      section: string | undefined;
    }
  ];
  ngExpand: [methodId: string];
}>();

useEventListener("ng:search", (event) => {
  emit("ngSearch", event.detail);

  // Prevent event from being handled multiple times
  // if more than one LeNgArticle is present
  event.stopImmediatePropagation();
});

useEventListener("ng:article-expanded", (event) => {
  emit("ngExpand", event.detail.methodId);

  // Prevent event from being handled multiple times
  // if more than one LeNgArticle is present
  event.stopImmediatePropagation();
});
</script>

<template>
  <div class="le-ng-article" :class="{ 'le-ng-article--with-blur': props.blurred }">
    <div v-if="props.blurred" class="le-ng-article__blur"></div>

    <slot name="prefix" />

    <div v-if="props.prefix" v-html="props.prefix"></div>

    <LeNgArticleClassic
      v-if="props.searchViewType === 'classic'"
      class="le-ng-article__box article-box"
      :method-id="props.methodId"
      :html="props.html"
      :index="props.index"
    />

    <LeNgArticleCompact
      v-if="props.searchViewType === 'compact'"
      class="le-ng-article__box article-box"
      :method-id="props.methodId"
      :html="props.html"
      :index="props.index"
      @expand="emit('ngExpand', props.methodId)"
    />
  </div>
</template>

<style scoped lang="less">
@import "@dsk/styles/all";
@import "@/assets/styles/z-index.less";

.le-ng-article {
  &--with-blur {
    position: relative;
  }

  &__box {
    max-width: calc(100vw - 30px);
    border-radius: 4px;
    background-color: white;
    box-shadow: @shadows[floating-default];
    color: black;
    font-family: Merriweather, serif;
    font-size: 14px;

    > * {
      overflow-x: auto;
    }
  }

  &__blur {
    position: absolute;
    z-index: @le-ng-article__blur-overlay-z-index;
    width: 100%;
    height: 100%;
    border-radius: 4px;
    backdrop-filter: blur(5px);
  }
}

// Old articles need this class on a article-container element
::v-deep(.article-box) {
  margin: 0;

  // Avoid double spacing from parent's row-gap
  ::v-deep(.cent-music-content) {
    margin-bottom: 0;
  }
}

::v-deep(.article-prefix) {
  margin-bottom: -3px;
  padding: 10px 30px 13px;
  border: none;
  border-radius: 4px 4px 0px 0px;
  background: @colors[@primary][100];
  .body-text("m");
  color: @colors[@neutrals][300];
}
</style>
